/**
 * Custom hook for loading configuration values from the database
 * Uses React Query for automatic caching and deduplication
 */
import { useQuery } from '@tanstack/react-query';
import { getConfigByCategory } from '../services/configService';

/**
 * Hook for loading configuration values (returns configKey strings only)
 * Uses React Query with long stale time since config rarely changes
 */
export function useConfig(categoryName: string) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['config', categoryName],
    queryFn: () => getConfigByCategory(categoryName),
    staleTime: 30 * 60 * 1000, // 30 minutes - config rarely changes
    gcTime: 60 * 60 * 1000, // 1 hour - keep in cache for 1 hour
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Extract just the configKey values in order
  const values = data?.map(item => item.configKey) || [];

  return {
    values,
    loading: isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to load configuration') : null
  };
}

/**
 * Hook for loading configuration values with display labels
 * Uses React Query with long stale time since config rarely changes
 */
export function useConfigWithLabels(categoryName: string) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['config', categoryName, 'withLabels'],
    queryFn: () => getConfigByCategory(categoryName),
    staleTime: 30 * 60 * 1000, // 30 minutes - config rarely changes
    gcTime: 60 * 60 * 1000, // 1 hour - keep in cache for 1 hour
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  return {
    values: data || [],
    loading: isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to load configuration') : null
  };
}
